import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { Breadcrumb } from 'components/breadcrumb'
import { parseDateFromLocalStorage } from 'components/form'
import { PageLoading } from 'components/loading'
import { confirm } from 'components/modals/confirm'
import { startOfDay } from 'date-fns'
import { useAtualizarContatoCidadaoMutation, useSalvarCuidadoCompartilhadoMutation } from 'graphql/hooks.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import { useLocalStorageState } from 'hooks/useStorage'
import { cloneDeep } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { convertConsultaModelToUpdateContatoCidadaoInput } from 'view/agenda/converter-agenda'
import { useVerificarAgendamentosConflitantes } from 'view/agenda/hooks/useVerificarAgendamentosConflitantes'
import { usePeriodoGestacional } from 'view/atendimentos/detail/soap/aside/pre-natal/hooks/usePeriodoGestacional'

import { CuidadoCompartilhadoHeaderTabs } from '../components/CuidadoCompartilhadoHeaderTabs'
import { LCC_TABS_ROUTE } from '../CuidadosCompartilhadosRootView'
import {
  AgendarConsultaCuidadoCompartilhadoFieldsModel,
  CidadaoCuidadoCompartilhado,
  CondutaGroupEnum,
  CuidadoCompartilhadoModel,
  DiscussaoCasoFormModel,
  ResponsavelCuidadoCompartilhado,
  TipoAgendamentoCuidadoCompartilhado,
} from '../model-cuidadoCompartilhado'
import {
  computeInitialValues,
  getFieldsByResponsavel,
  getResponsabilidadeCuidadoCompartilhado,
} from '../util-cuidadoCompartilhado'
import { convertDiscussaoCasoFormModelToSalvarCuidadoCompartilhadoInput } from './discussao-caso/converter-discussaoCaso'
import { DiscussaoCasoView } from './discussao-caso/DiscussaoCasoView'
import { logRegistrarDiscussao } from './logRegistrarDiscussao'

interface CuidadoCompartilhadoViewProps {
  cuidadoCompartilhado: CuidadoCompartilhadoModel
}

export const CuidadoCompartilhadoView = (props: CuidadoCompartilhadoViewProps) => {
  const { cuidadoCompartilhado } = props

  const alert = useAlert()
  const { getServerTimeNow } = useServerTime()
  const history = useHistory()
  const match = useRouteMatch()
  const { acesso, profissional } = useAcessoLotacaoOrEstagio()
  const { analytics } = useFirebase()
  const { verificarAgendamentosConflitantes } = useVerificarAgendamentosConflitantes()
  const [salvar] = useSalvarCuidadoCompartilhadoMutation()
  const videochamadaUuidState = useState<string>()
  const [videochamadaUuid] = videochamadaUuidState

  const responsavelCuidado: ResponsavelCuidadoCompartilhado = getResponsabilidadeCuidadoCompartilhado(
    acesso.id,
    cuidadoCompartilhado.lotacaoExecutanteAtual.id,
    cuidadoCompartilhado.lotacaoSolicitante.id
  )

  const prontuario = cuidadoCompartilhado.cidadao.prontuario
  const dataAtendimento = Number(startOfDay(getServerTimeNow()))
  const ultimaDumPreNatalAtivo = prontuario?.preNatalAtivo?.ultimaDum

  const { loading: loadingDataPeriodoGestacional, idadeGestacional } = usePeriodoGestacional({
    prontuarioId: prontuario.id,
    dum: ultimaDumPreNatalAtivo,
    dataAtendimento,
  })

  const [cacheState, setCacheState, deleteCacheState] = useLocalStorageState<DiscussaoCasoFormModel>(
    `${acesso.id}/cuidado-compartilhado/${cuidadoCompartilhado.id}`,
    { checkVersionCompatibility: true, modifier: cacheParser }
  )

  const [initialStateLoaded, setInitialStateLoaded] = useState(!!cacheState)
  const [initialStateLoadedObito, setInitialStateLoadedObito] = useState(!cuidadoCompartilhado.cidadao.faleceu)

  const goBackRoute = `${match.path.substring(0, match.path.lastIndexOf('/', match.path.lastIndexOf('/') - 1))}/${
    LCC_TABS_ROUTE[responsavelCuidado]
  }`

  const goBack = useCallback(() => history.push(goBackRoute), [goBackRoute, history])

  const [atualizarContatoCidadao] = useAtualizarContatoCidadaoMutation()

  const updateContatoCidadao = useCallback(
    async (values: AgendarConsultaCuidadoCompartilhadoFieldsModel['consultaCompartilhada']) => {
      const cidadao = values?.cidadaoParticipante?.cidadao
      const contatoCidadao = cidadao?.contato
      const contatoAtualizado = values?.cidadaoParticipante?.contato

      const telefoneCelularAlterado = contatoCidadao?.telefoneCelular !== contatoAtualizado?.telefoneCelular
      const emailAlterado = contatoCidadao?.email !== contatoAtualizado?.email

      if (cidadao && (telefoneCelularAlterado || emailAlterado)) {
        try {
          return await atualizarContatoCidadao({
            variables: {
              input: convertConsultaModelToUpdateContatoCidadaoInput(cidadao.id, values.cidadaoParticipante.contato),
            },
          })
        } catch (err) {
          alert('danger', 'Erro ao salvar dados do cidadão.')
          throw err
        }
      }
    },
    [alert, atualizarContatoCidadao]
  )

  const handleSave = useCallback(
    async (values: DiscussaoCasoFormModel) => {
      await salvar({
        variables: {
          input: convertDiscussaoCasoFormModelToSalvarCuidadoCompartilhadoInput(
            values,
            cuidadoCompartilhado,
            acesso.id,
            videochamadaUuid
          ),
        },
      })
      alert('success', 'Conduta para cuidado compartilhado registrada com sucesso.')
      logRegistrarDiscussao(analytics.logEvent, values, responsavelCuidado)
      deleteCacheState(false)
      goBack()
    },
    [
      acesso.id,
      alert,
      analytics.logEvent,
      cuidadoCompartilhado,
      deleteCacheState,
      goBack,
      responsavelCuidado,
      salvar,
      videochamadaUuid,
    ]
  )

  const handleSubmit = useCallback(
    async (values: DiscussaoCasoFormModel) => {
      const fields = getFieldsByResponsavel(responsavelCuidado, values)

      if (fields?.condutaGroup === CondutaGroupEnum.AGENDAR_CONSULTA) {
        const { cidadaoId, cidadaoNome, agendamentos } = getAgendamentosConflitantesInput(
          acesso.id,
          profissional.id,
          cuidadoCompartilhado.cidadao,
          fields?.[CondutaGroupEnum.AGENDAR_CONSULTA]
        )

        if (
          fields?.[CondutaGroupEnum.AGENDAR_CONSULTA]?.tipoAgendamento ===
          TipoAgendamentoCuidadoCompartilhado.CONSULTA_COMPARTILHADA
        ) {
          await updateContatoCidadao(fields[CondutaGroupEnum.AGENDAR_CONSULTA].consultaCompartilhada)
        }

        const confirmouHorariosConflitantes = await verificarAgendamentosConflitantes(
          cidadaoId,
          agendamentos,
          cidadaoNome
        )

        if (!confirmouHorariosConflitantes) return
      }

      return handleSave(values)
    },
    [
      acesso.id,
      cuidadoCompartilhado.cidadao,
      handleSave,
      profissional.id,
      responsavelCuidado,
      updateContatoCidadao,
      verificarAgendamentosConflitantes,
    ]
  )

  const handleCancel = () => {
    confirm({
      title: 'Deseja cancelar o cuidado compartilhado?',
      body: 'As alterações realizadas serão perdidas.',
      confirmLabel: 'Sim',
      cancelLabel: 'Não',
      onConfirm: () => {
        deleteCacheState(false)
        goBack()
      },
    })()
  }

  useEffect(() => {
    if (!initialStateLoaded || !initialStateLoadedObito) {
      const initialValues = computeInitialValues(responsavelCuidado, cuidadoCompartilhado.cidadao)
      setCacheState(initialValues)
      setInitialStateLoaded(true)
      setInitialStateLoadedObito(true)
    }
  }, [cuidadoCompartilhado.cidadao, initialStateLoaded, initialStateLoadedObito, responsavelCuidado, setCacheState])

  const renderDiscussaoCaso = (cidadao: CidadaoCuidadoCompartilhado, headerRef: React.RefObject<HTMLDivElement>) => (
    <DiscussaoCasoView
      cuidadoCompartilhado={cuidadoCompartilhado}
      cidadao={cidadao}
      headerRef={headerRef}
      cacheState={cacheState}
      responsavelCuidado={responsavelCuidado}
      videochamadaUuidState={videochamadaUuidState}
      updateCacheState={setCacheState}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      idadeGestacional={idadeGestacional}
    />
  )

  if (!initialStateLoaded || loadingDataPeriodoGestacional || !initialStateLoadedObito) {
    return <PageLoading message='Carregando dados do cuidado compartilhado...' />
  }

  return (
    <>
      <Breadcrumb title='Discutir caso' />
      <CuidadoCompartilhadoHeaderTabs
        cuidadoCompartilhado={cuidadoCompartilhado}
        idadeGestacional={idadeGestacional}
        renderDiscussaoCaso={renderDiscussaoCaso}
        handleCancel={handleCancel}
        isVisualizacao={false}
      />
    </>
  )
}

const cacheParser = (values: DiscussaoCasoFormModel) => {
  const clone = cloneDeep(values)

  if (clone?.executanteFields?.[CondutaGroupEnum.AGENDAR_CONSULTA]) {
    clone.executanteFields[CondutaGroupEnum.AGENDAR_CONSULTA] = parseAgendamento(
      clone.executanteFields[CondutaGroupEnum.AGENDAR_CONSULTA]
    )
  }

  if (clone?.solicitanteFields?.[CondutaGroupEnum.AGENDAR_CONSULTA]) {
    clone.solicitanteFields[CondutaGroupEnum.AGENDAR_CONSULTA] = parseAgendamento(
      clone.solicitanteFields[CondutaGroupEnum.AGENDAR_CONSULTA]
    )
  }

  return clone
}

const parseAgendamento = (
  input: AgendarConsultaCuidadoCompartilhadoFieldsModel
): AgendarConsultaCuidadoCompartilhadoFieldsModel => {
  const { tipoAgendamento, consultaComCidadao, consultaCompartilhada } = input ?? {}

  return {
    tipoAgendamento,
    consultaComCidadao: consultaComCidadao
      ? { ...consultaComCidadao, horario: parseDateFromLocalStorage(consultaComCidadao.horario) }
      : null,
    consultaCompartilhada: consultaCompartilhada
      ? { ...consultaCompartilhada, horario: parseDateFromLocalStorage(consultaCompartilhada.horario) }
      : null,
  }
}

const getAgendamentosConflitantesInput = (
  lotacaoId: ID,
  profissionalId: ID,
  cidadao: CuidadoCompartilhadoModel['cidadao'],
  agendamento: AgendarConsultaCuidadoCompartilhadoFieldsModel
) => {
  const horarioInicial =
    agendamento.tipoAgendamento === TipoAgendamentoCuidadoCompartilhado.CONSULTA_COM_CIDADAO
      ? agendamento.consultaComCidadao?.horario?.inicial
      : agendamento.consultaCompartilhada?.horario?.inicial

  return {
    cidadaoId: cidadao.id,
    cidadaoNome: cidadao.nomeSocial ?? cidadao.nome,
    agendamentos: [
      {
        horario: Number(horarioInicial),
        lotacaoId,
        profissionalId,
      },
    ],
  }
}
